import { ArrowClockwiseIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import * as Tabs from '@radix-ui/react-tabs'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { GhostButton } from '../../components/buttons/ghost-button.tsx'
import { ErrorBoundary } from '../../components/error-boundaries/error-boundary'
import { Spinner } from '../../components/spinner.tsx'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import {
  invalidateGetRepositoryThreatModel,
  useGetRepository,
  useGetRepositoryThreatModel,
  useGetRepositoryThreatModelContent,
  usePostRepositoryThreatModel,
  useThreatModelStream,
} from '../../utils/api-client/api-hooks'
import {
  isInFlightGenerationStatus,
  type ThreatModelContent as ThreatModelContentType,
  type ThreatModelPhase,
  ThreatModelPhaseSchema,
} from '../../utils/api-client/api-schemas.ts'
import { formatDate, formatRelativeTime } from '../../utils/date-utils.ts'
import { joinMetaSegments } from '../../utils/text-utils.ts'
import { PhaseStepper } from './phase-stepper.tsx'
import { useRepositoryDetailsPageParams } from './repository-details-page.tsx'
import { patchSearchParams } from './scans-list.tsx'
import { ScorecardsTab } from './scorecards-tab.tsx'
import { ThreatCoverageGraph } from './threat-coverage-graph.tsx'
import { type ThreatModelCoverageInput, type ThreatRelevance } from './threat-coverage.ts'
import { ThreatModelGraph } from './threat-model-graph.tsx'
import {
  parseSurfaceSection,
  SURFACE_SECTION_PARAM,
  SURFACE_VIEW_PARAM,
  THREAT_MODEL_VIEW_PARAM,
} from './threat-model-navigation.ts'
import {
  type Relevance,
  RelevanceFilter,
  ThreatModelAdjudication,
  ThreatModelAttackSurface,
  ThreatModelOverview,
} from './threat-model-report.tsx'
import * as styles from './threat-model-tab.css.ts'
import { UpdateHistoryView } from './update-history-view.tsx'

// `unknown` is a fallback the backend reports when it can't determine the live phase; it is not a
// real generation step, so it is excluded from the stepper's ordered phases.
type SupportedThreatModelPhase = Exclude<ThreatModelPhase, 'unknown'>

// The type predicate narrows the element type so `SUPPORTED_THREAT_MODEL_PHASES` is
// `SupportedThreatModelPhase[]`, which forces callers to reconcile the wider `unknown`-bearing phase
// (via `toSupportedPhase`) before it can reach the stepper.
const SUPPORTED_THREAT_MODEL_PHASES = ThreatModelPhaseSchema.options.filter(
  (phase): phase is SupportedThreatModelPhase => phase !== 'unknown'
)

// The live phase can be `unknown` (indeterminate); the stepper only knows the real phases, so map
// `unknown` to the first phase to render an in-flight state instead of mis-indexing on it.
const toSupportedPhase = (phase: ThreatModelPhase): SupportedThreatModelPhase =>
  phase === 'unknown' ? 'queued' : phase

const PHASE_LABELS = {
  queued: 'Queued',
  fetching_code: 'Fetching code',
  exploring_codebase: 'Exploring codebase',
  modeling_threats: 'Modeling threats',
  rendering: 'Rendering',
} satisfies Record<SupportedThreatModelPhase, string>

/**
 * The document splits into three views because its sections answer three different questions on three
 * different rhythms — see `threat-model-report.tsx`. The graph is a *representation* of the attack
 * surface rather than a peer of it, so it lives inside that view rather than taking a sub-tab.
 *
 * **Order runs from the model outwards.** Overview frames the system and Attack surface is the model
 * itself; Scorecard is the judgement derived from it, and Adjudication the guidance derived from that.
 * History is provenance, so it closes. Adjudication can sit late because it is arrived at knowing which
 * vulnerability class you want, not scrolled to.
 */
const ThreatModelViewSchema = z.enum(['overview', 'surface', 'scorecards', 'adjudication', 'history'])
type ThreatModelView = z.infer<typeof ThreatModelViewSchema>
const parseThreatModelView = (raw: string | null): ThreatModelView =>
  ThreatModelViewSchema.safeParse(raw).data ?? 'overview'

export const ThreatModelTab: React.FC = () => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const view = parseThreatModelView(searchParams.get(THREAT_MODEL_VIEW_PARAM))
  const setView = useCallback(
    (nextView: ThreatModelView) => {
      setSearchParams(prev =>
        patchSearchParams(prev, {
          [THREAT_MODEL_VIEW_PARAM]: nextView === 'overview' ? null : nextView,
          // The section highlight marks an arrival from an Overview metric, so it lasts as long as that
          // arrival does. Any sub-tab click is the reader moving on under their own steam — coming back
          // to Attack surface later should not re-announce a section they already found.
          [SURFACE_SECTION_PARAM]: null,
        })
      )
    },
    [setSearchParams]
  )

  return (
    <Tabs.Root value={view} onValueChange={value => setView(value as ThreatModelView)}>
      <Tabs.List className={styles.subTabList} aria-label="Threat model views">
        <Tabs.Trigger value="overview" className={styles.subTabTrigger}>
          Overview
        </Tabs.Trigger>
        <Tabs.Trigger value="surface" className={styles.subTabTrigger}>
          Attack surface
        </Tabs.Trigger>
        <Tabs.Trigger value="scorecards" className={styles.subTabTrigger}>
          Scorecard
        </Tabs.Trigger>
        <Tabs.Trigger value="adjudication" className={styles.subTabTrigger}>
          Adjudication
        </Tabs.Trigger>
        <Tabs.Trigger value="history" className={styles.subTabTrigger}>
          History
        </Tabs.Trigger>
      </Tabs.List>
      <Tabs.Content value="overview" tabIndex={-1}>
        <ThreatModelDocumentView repositoryId={repositoryId} mode="overview" />
      </Tabs.Content>
      <Tabs.Content value="surface" tabIndex={-1}>
        <ThreatModelDocumentView repositoryId={repositoryId} mode="surface" />
      </Tabs.Content>
      <Tabs.Content value="scorecards" tabIndex={-1}>
        <ErrorBoundary fallbackTitle="Failed to load scorecards">
          <ScorecardsTab onSwitchToThreatModel={() => setView('overview')} />
        </ErrorBoundary>
      </Tabs.Content>
      <Tabs.Content value="adjudication" tabIndex={-1}>
        <ThreatModelDocumentView repositoryId={repositoryId} mode="adjudication" />
      </Tabs.Content>
      <Tabs.Content value="history" tabIndex={-1}>
        <ErrorBoundary fallbackTitle="Failed to load update history">
          <UpdateHistoryView repositoryId={repositoryId} />
        </ErrorBoundary>
      </Tabs.Content>
    </Tabs.Root>
  )
}

/** Which of the three report views the completed content renders as. */
type ThreatModelContentMode = 'overview' | 'surface' | 'adjudication'

type ThreatModelDocumentViewProps = {
  repositoryId: string
  mode: ThreatModelContentMode
}

const ThreatModelDocumentView: React.FC<ThreatModelDocumentViewProps> = ({ repositoryId, mode }) => {
  const queryClient = useQueryClient()
  // Owned here rather than inside the adjudication view, because the control renders in the meta row.
  const [selectedRelevances, setSelectedRelevances] = useState<Relevance[]>([])
  const { data: repository } = useGetRepository({ repositoryId })
  const { data: threatModel, isLoading } = useGetRepositoryThreatModel({ repositoryId })

  // Any viewer's GET can report an in-flight status (single-flight generation may have been
  // triggered elsewhere); the stream and polling both key off the server state. `unknown` counts as
  // in-flight so the stream/poll keep running until the status resolves — see the render `match`.
  const isInFlight = !!threatModel && isInFlightGenerationStatus(threatModel.status)
  const stream = useThreatModelStream(isInFlight ? repositoryId : undefined)
  const lastStreamEvent = stream.events.at(-1)
  const firstStreamEvent = stream.events.at(0)

  useEffect(() => {
    if (lastStreamEvent && lastStreamEvent.status !== 'processing') {
      invalidateGetRepositoryThreatModel(queryClient, { repositoryId })
    }
  }, [lastStreamEvent, queryClient, repositoryId])

  const showGenerateError = useShowMutationError<typeof usePostRepositoryThreatModel>({
    prefix: 'Failed to start threat model generation',
  })
  const generateMutation = usePostRepositoryThreatModel({
    onSuccess: () => invalidateGetRepositoryThreatModel(queryClient, { repositoryId }),
    onError: showGenerateError,
  })
  const isStarting = generateMutation.isPending
  // Depend on the stable `mutate`, not the mutation object (which changes on every state flip).
  const onGenerate = useCallback(
    () => generateMutation.mutate({ repositoryId }),
    [generateMutation.mutate, repositoryId]
  )

  // The content endpoint always serves the latest *completed* document, so the
  // previous model stays visible while a regeneration is in flight or has failed.
  const hasCompletedContent = !!threatModel?._links.content
  const { data: content, isLoading: isContentLoading } = useGetRepositoryThreatModelContent({
    repositoryId,
    generatedAt: threatModel?.generatedAt,
    enabled: hasCompletedContent,
  })

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading threat model" />
      </div>
    )
  }

  if (!threatModel) {
    return <ThreatModelEmptyState onGenerate={onGenerate} isStarting={isStarting} />
  }

  const currentPhase = toSupportedPhase(lastStreamEvent?.phase ?? threatModel.phase)
  const startedAt = firstStreamEvent?.timestamp

  return (
    <div className={styles.container}>
      {match(threatModel.status)
        // `unknown` is an indeterminate status; treat it as in-flight so polling can resolve it.
        .with('processing', 'unknown', () =>
          hasCompletedContent ? (
            <>
              <ThreatModelRegeneratingBanner
                phase={currentPhase}
                requestedBy={threatModel.requestedBy}
                startedAt={startedAt}
              />
              <ThreatModelContent content={content} isLoading={isContentLoading} mode={mode} dimmed={true} />
            </>
          ) : (
            <ThreatModelGeneratingState
              phase={currentPhase}
              requestedBy={threatModel.requestedBy}
              startedAt={startedAt}
            />
          )
        )
        .with('failed', () => (
          <>
            <ThreatModelFailedBanner
              failureReason={threatModel.failureReason}
              onRetry={onGenerate}
              isRetrying={isStarting}
            />
            {hasCompletedContent && <ThreatModelContent content={content} isLoading={isContentLoading} mode={mode} />}
          </>
        ))
        .with('completed', () => (
          <>
            <ThreatModelMetaRow
              generatedAt={threatModel.generatedAt}
              branch={repository?.default_branch}
              commitSha={threatModel.commitSha}
              requestedBy={threatModel.requestedBy}
              onRegenerate={onGenerate}
              isStarting={isStarting}
              action={
                mode === 'adjudication' && content ? (
                  <RelevanceFilter content={content} selected={selectedRelevances} onChange={setSelectedRelevances} />
                ) : undefined
              }
            />
            <ThreatModelContent
              content={content}
              isLoading={isContentLoading}
              mode={mode}
              selectedRelevances={selectedRelevances}
            />
          </>
        ))
        .exhaustive()}
    </div>
  )
}

type ThreatModelEmptyStateProps = {
  onGenerate: () => unknown
  isStarting: boolean
}

const ThreatModelEmptyState: React.FC<ThreatModelEmptyStateProps> = ({ onGenerate, isStarting }) => (
  <div className={styles.emptyState}>
    <p className={styles.emptyStateTitle}>No threat model yet</p>
    <p className={styles.emptyStateDescription}>
      Generate a threat model to map this repository's data flows, trust boundaries, and the threats that matter most.
      Generation usually takes 5–10 minutes.
    </p>
    <DefaultButton buttonType="primary" state={isStarting ? 'loading' : 'default'} onClick={onGenerate}>
      Generate threat model
    </DefaultButton>
  </div>
)

type ThreatModelGeneratingStateProps = {
  phase: SupportedThreatModelPhase
  requestedBy?: string
  startedAt?: string
}

const ThreatModelGeneratingState: React.FC<ThreatModelGeneratingStateProps> = ({ phase, requestedBy, startedAt }) => (
  <div className={styles.generatingState}>
    <PhaseStepper
      phases={SUPPORTED_THREAT_MODEL_PHASES}
      labels={PHASE_LABELS}
      currentPhase={phase}
      currentColor={mappedColors.primary.pulse}
      // White on pulse is 3.86:1; this fixed dark navy holds 4.51:1 in both themes.
      currentTextColor={mappedColors.severity.text}
    />
    <div className={styles.generatingMeta}>
      <p className={styles.generatingTitle}>
        <Spinner size="16" label="Generating threat model" />
        Generating threat model
      </p>
      <p className={styles.metaText}>
        {joinMetaSegments([
          requestedBy && `Requested by ${requestedBy}`,
          startedAt && `Started ${formatRelativeTime(startedAt)}`,
        ])}
      </p>
      <p className={styles.hintText}>
        Usually takes 5–10 minutes. You can leave this page — generation continues in the background.
      </p>
    </div>
  </div>
)

type ThreatModelRegeneratingBannerProps = {
  phase: SupportedThreatModelPhase
  requestedBy?: string
  startedAt?: string
}

const ThreatModelRegeneratingBanner: React.FC<ThreatModelRegeneratingBannerProps> = ({
  phase,
  requestedBy,
  startedAt,
}) => (
  <div className={styles.progressBanner} role="status" aria-label="Regenerating threat model">
    <p className={styles.progressTitle}>
      <Spinner size="20" />
      Regenerating threat model
    </p>
    <p className={styles.progressDescription}>
      {joinMetaSegments([
        `Regenerating — ${PHASE_LABELS[phase]} (${SUPPORTED_THREAT_MODEL_PHASES.indexOf(phase) + 1}/${SUPPORTED_THREAT_MODEL_PHASES.length})`,
        requestedBy && `requested by ${requestedBy}`,
        startedAt && `started ${formatRelativeTime(startedAt)}`,
      ])}
    </p>
  </div>
)

type ThreatModelFailedBannerProps = {
  failureReason: string | null | undefined
  onRetry: () => unknown
  isRetrying: boolean
}

const ThreatModelFailedBanner: React.FC<ThreatModelFailedBannerProps> = ({ failureReason, onRetry, isRetrying }) => (
  <div className={styles.failedState} role="alert">
    <p className={styles.failedStateTitle}>
      <WarningCircleIcon size={20} weight="fill" className={styles.failedStateIcon} />
      Threat model generation failed
    </p>
    <p className={styles.failedStateDescription}>
      {/* The reason comes from the analysis service when it has one; otherwise say something rather than
          leaving the card with a title and nothing else. */}
      {failureReason ?? 'Something went wrong while generating the threat model.'}
    </p>
    <GhostButton
      size="small"
      // GhostButton has no loading state, so an in-flight retry disables it rather than spinning.
      state={isRetrying ? 'disabled' : 'default'}
      icon={<ArrowClockwiseIcon size={14} weight="light" />}
      onClick={onRetry}
    >
      Try again
    </GhostButton>
  </div>
)

type ThreatModelMetaRowProps = {
  generatedAt: string | null | undefined
  branch?: string
  commitSha?: string
  requestedBy?: string
  onRegenerate: () => unknown
  isStarting: boolean
  /** Sits left of Regenerate — a control that acts on the view below, not on the model. */
  action?: React.ReactNode
}

const ThreatModelMetaRow: React.FC<ThreatModelMetaRowProps> = ({
  generatedAt,
  branch,
  commitSha,
  requestedBy,
  onRegenerate,
  isStarting,
  action,
}) => {
  const shortSha = commitSha?.slice(0, 7)
  const provenance = [branch, shortSha ? `@ ${shortSha}` : undefined].filter(Boolean).join(' ')

  return (
    <div className={styles.metaRow}>
      <p className={styles.metaText}>
        {joinMetaSegments([
          generatedAt && `Generated ${formatDate({ date: generatedAt, type: 'mm/dd/yyyy-hh:mm' })}`,
          provenance,
          requestedBy && `requested by ${requestedBy}`,
        ])}
      </p>
      <div className={styles.metaActions}>
        {action}
        <DefaultButton
          buttonType="secondary"
          size="small"
          state={isStarting ? 'loading' : 'default'}
          onClick={onRegenerate}
        >
          Regenerate
        </DefaultButton>
      </div>
    </div>
  )
}

type ThreatModelContentProps = {
  content?: ThreatModelContentType
  isLoading: boolean
  mode: ThreatModelContentMode
  dimmed?: boolean
  selectedRelevances?: Relevance[]
}

const ThreatModelContent: React.FC<ThreatModelContentProps> = ({
  content,
  isLoading,
  mode,
  dimmed = false,
  selectedRelevances,
}) => {
  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading threat model content" />
      </div>
    )
  }

  return (
    <div className={classNames(styles.content, { [styles.contentDimmed]: dimmed })} data-testid="threat-model-content">
      {content ? (
        match(mode)
          .with('overview', () => <ThreatModelOverview content={content} />)
          .with('surface', () => <AttackSurfaceView content={content} />)
          .with('adjudication', () => <ThreatModelAdjudication content={content} selected={selectedRelevances} />)
          .exhaustive()
      ) : (
        // Reaching here means the query settled (not loading) with no parsed content — a rare
        // malformed/empty `/content` payload that failed schema validation. Degrade to an explicit
        // message rather than a blank panel.
        <div className={styles.unavailableState}>
          <p className={styles.unavailableStateTitle}>Threat model content is unavailable</p>
          <p className={styles.unavailableStateDescription}>
            The generated content couldn&apos;t be loaded. Regenerating the threat model usually resolves it.
          </p>
        </div>
      )}
    </div>
  )
}

const SURFACE_VIEWS = [
  { id: 'coverage', label: 'Threat coverage' },
  { id: 'zones', label: 'Trust zones' },
  { id: 'list', label: 'Lists' },
] as const
type SurfaceView = (typeof SURFACE_VIEWS)[number]['id']

const parseSurfaceView = (raw: string | null): SurfaceView =>
  SURFACE_VIEWS.find(view => view.id === raw)?.id ?? 'coverage'

/**
 * The attack surface and its two graphs.
 *
 * All three are derived from the same inventory this view already carries — coverage from
 * `security_controls` x `vulnerability_class_impacts`, trust zones from `actors`, `entry_points` and
 * `sensitive_assets` — so they are *representations* of this view rather than peers of it. That is what
 * the third tab level encodes: same shape as the tabs above, a fraction of their weight.
 */
const AttackSurfaceView: React.FC<{ content: ThreatModelContentType }> = ({ content }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const surfaceView = parseSurfaceView(searchParams.get(SURFACE_VIEW_PARAM))
  const openSection = parseSurfaceSection(searchParams.get(SURFACE_SECTION_PARAM))

  // Changing tertiary tab by hand ends the arrival the metric began, so the highlight goes with it.
  const setSurfaceView = (next: SurfaceView) =>
    setSearchParams(prev =>
      patchSearchParams(prev, {
        [SURFACE_VIEW_PARAM]: next === 'coverage' ? null : next,
        [SURFACE_SECTION_PARAM]: null,
      })
    )

  return (
    <Tabs.Root
      value={surfaceView}
      onValueChange={next => setSurfaceView(next as SurfaceView)}
      className={styles.surfaceView}
    >
      <Tabs.List className={styles.surfaceViewTabs} aria-label="Attack surface view">
        {SURFACE_VIEWS.map(view => (
          <Tabs.Trigger key={view.id} value={view.id} className={styles.surfaceViewTab}>
            {view.label}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      <Tabs.Content value="coverage" tabIndex={-1}>
        <ThreatCoverageGraph {...toCoverageInput(content)} />
      </Tabs.Content>
      <Tabs.Content value="zones" tabIndex={-1}>
        <ThreatModelGraph content={content} />
      </Tabs.Content>
      <Tabs.Content value="list" tabIndex={-1}>
        <ThreatModelAttackSurface content={content} openSection={openSection} />
      </Tabs.Content>
    </Tabs.Root>
  )
}

/** Relevance arrives as free text on the payload; map it onto the coverage graph's fixed vocabulary. */
const coerceRelevance = (relevance: string): ThreatRelevance =>
  match(relevance.trim().toLowerCase())
    .with('present', () => 'present' as const)
    .with('likely', () => 'likely' as const)
    .with('unlikely', () => 'unlikely' as const)
    .with('not_applicable', 'not applicable', () => 'not_applicable' as const)
    // An unrecognised relevance stays visible as a low-confidence threat rather than being dropped.
    .otherwise(() => 'unlikely' as const)

/**
 * Adapts the snake_case payload into the coverage graph's input. The graph derives every node and edge
 * from `security_controls[].mitigates` and `vulnerability_class_impacts[]` alone — no prose is read.
 */
const toCoverageInput = (content: ThreatModelContentType): ThreatModelCoverageInput => ({
  internetFacing: content.exposure.internet_facing,
  controls: content.security_controls.map(control => ({
    name: control.name,
    kind: control.kind,
    mitigates: control.mitigates,
    scope: control.scope,
  })),
  vulnerabilities: content.vulnerability_class_impacts.map(impact => ({
    vulnClass: impact.vuln_class,
    relevance: coerceRelevance(impact.relevance),
    reachability: impact.reachability,
    blastRadius: impact.blast_radius,
  })),
})
